import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { banners } from "@/mocks/banners";
import { getMovieById, typeLabels } from "@/utils/media";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  const go = useCallback((next: number) => {
    setCurrent((next + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden bg-foreground-950">
      {banners.map((banner, index) => {
        const movie = getMovieById(banner.movieId);
        return (
          <div
            key={banner.movieId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === current ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <img
              src={banner.image}
              alt={movie ? `${movie.title} 焦点推荐` : "焦点推荐"}
              title={movie ? `${movie.title} 在线观看` : "向日葵视频焦点推荐"}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>

            <div className="relative z-10 h-full w-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col justify-center">
              <div className="max-w-2xl">
                {movie && (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="h-6 px-2.5 flex items-center rounded bg-primary-500 text-foreground-950 text-xs font-bold">
                        {typeLabels[movie.type]}
                      </span>
                      <span className="h-6 px-2.5 flex items-center rounded bg-background-50/15 text-background-50 text-xs font-medium backdrop-blur-sm">
                        {movie.year} · {movie.region}
                      </span>
                      <span className="h-6 px-2.5 flex items-center gap-1 rounded bg-background-50/15 text-primary-400 text-xs font-semibold backdrop-blur-sm">
                        <i className="ri-star-fill text-[11px]"></i>
                        {movie.rating.toFixed(1)}
                      </span>
                    </div>
                    <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-background-50 tracking-tight">
                      {movie.title}
                    </h2>
                    <p className="mt-3 md:mt-4 text-sm md:text-base text-background-200 max-w-xl leading-relaxed line-clamp-2">
                      {banner.tagline}｜{movie.synopsis}
                    </p>
                    <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
                      <Link
                        to={`/detail/${movie.id}`}
                        className="h-11 md:h-12 px-6 flex items-center gap-2 rounded-md bg-primary-500 text-foreground-950 font-bold text-sm md:text-base hover:bg-primary-400 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-play-circle-line text-xl"></i>
                        立即观看
                      </Link>
                      <Link
                        to={`/detail/${movie.id}`}
                        className="h-11 md:h-12 px-6 flex items-center gap-2 rounded-md bg-background-50/15 text-background-50 font-medium text-sm md:text-base hover:bg-background-50/25 transition-colors backdrop-blur-sm whitespace-nowrap"
                      >
                        <i className="ri-information-line text-xl"></i>
                        了解详情
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(current - 1)}
        className="hidden md:flex absolute z-20 left-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-primary-500 hover:text-foreground-950 transition-colors backdrop-blur-sm cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(current + 1)}
        className="hidden md:flex absolute z-20 right-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-primary-500 hover:text-foreground-950 transition-colors backdrop-blur-sm cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute z-20 bottom-5 md:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {banners.map((banner, index) => (
          <button
            key={banner.movieId}
            type="button"
            aria-label={`切换到第${index + 1}张`}
            onClick={() => setCurrent(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === current ? "w-8 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}